import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { PartyPopper, ArrowRight } from "lucide-react";

const API = `${window.__BACKEND__}/api`;

/**
 * HappyHourLiveBanner
 * -------------------
 * Sits on the homepage right under the primary CTAs.
 * Renders ONLY while a HappyHour event is live (backend `/api/happyhour/current`
 * returns `current !== null`). Yellow, pulsing, party-vibe, click → /happyhour.
 *
 * Width matches the row of 3 hero buttons (max 812px on desktop), full width on mobile.
 * Height matches a hero button (py-4 padding).
 */
export default function HappyHourLiveBanner() {
  const [live, setLive] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/happyhour/current`);
      setLive(r.data?.current || null);
    } catch (_) {
      setLive(null);
    }
  }, []);

  useEffect(() => {
    load();
    // Poll every 60s so the banner appears/disappears at the boundary
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <AnimatePresence>
      {live && (
        <motion.div
          key="hh-live-banner"
          initial={{ opacity: 0, y: -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98 }}
          transition={{ duration: 0.4 }}
          className="relative z-10 mt-4 sm:mt-5 flex justify-center w-full px-2"
          data-testid="happyhour-live-banner-wrap"
        >
          <Link
            to="/happyhour"
            data-testid="happyhour-live-banner"
            aria-label="È ora dell'HappyHour · vai alla pagina codici"
            className="hh-live-banner group relative w-full max-w-[812px] rounded-full overflow-hidden font-semibold text-black flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3.5 sm:py-4 text-[13px] sm:text-base"
            style={{
              background:
                "linear-gradient(90deg,#EA580C 0%,#ffe14a 50%,#EA580C 100%)",
              boxShadow:
                "0 0 32px rgba(234,88,12,0.55), 0 0 60px rgba(234,88,12,0.35)",
            }}
          >
            {/* Festive streamers — top */}
            <StreamersTop />
            {/* Festive streamers — bottom */}
            <StreamersBottom />

            <PartyPopper
              size={18}
              className="flex-none animate-hh-shake"
              strokeWidth={2.4}
            />
            <span className="text-center leading-tight">
              <span className="hidden xs:inline">È ora dell&apos;</span>
              <strong>HappyHour</strong>
              <span className="mx-1">·</span>
              <span className="text-black/80">veloce che i drink finiscono</span>
            </span>
            <ArrowRight
              size={16}
              className="flex-none group-hover:translate-x-1 transition-transform"
              strokeWidth={2.6}
            />

            {/* Shimmer sweep */}
            <span
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%)",
                animation: "hh-shimmer 3.4s linear infinite",
                backgroundSize: "220% 100%",
              }}
            />
          </Link>

          <style>{`
            .hh-live-banner { animation: hh-live-pulse 1.6s ease-in-out infinite; }
            @keyframes hh-live-pulse {
              0%, 100% { transform: scale(1); box-shadow: 0 0 26px rgba(234,88,12,0.5), 0 0 48px rgba(234,88,12,0.28); }
              50%      { transform: scale(1.015); box-shadow: 0 0 38px rgba(234,88,12,0.75), 0 0 72px rgba(234,88,12,0.45); }
            }
            @keyframes hh-shimmer {
              0%   { background-position: -120% 0; }
              100% { background-position: 220% 0; }
            }
            @keyframes hh-shake {
              0%, 100% { transform: rotate(-8deg); }
              50%      { transform: rotate(12deg); }
            }
            .animate-hh-shake { animation: hh-shake 0.9s ease-in-out infinite; transform-origin: 60% 60%; }
            @keyframes hh-streamer {
              0%   { transform: translateY(-2px) rotate(0deg); opacity: 0.9; }
              50%  { transform: translateY(3px)  rotate(6deg); opacity: 1; }
              100% { transform: translateY(-2px) rotate(0deg); opacity: 0.9; }
            }
            .animate-hh-streamer { animation: hh-streamer 1.4s ease-in-out infinite; }
            /* xs breakpoint fallback (Tailwind default doesn't ship 'xs') */
            @media (min-width: 400px) { .xs\\:inline { display: inline; } }
            @media (max-width: 399px) { .xs\\:inline { display: none; } }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Small festive streamer shapes at top of the banner
function StreamersTop() {
  const dots = [
    { left: "6%",  color: "#ff4fcf", delay: "0s"    },
    { left: "20%", color: "#4fc3ff", delay: "0.15s" },
    { left: "34%", color: "#ff8a1f", delay: "0.3s"  },
    { left: "48%", color: "#a78bfa", delay: "0.45s" },
    { left: "62%", color: "#ff4fcf", delay: "0.6s"  },
    { left: "76%", color: "#4fc3ff", delay: "0.15s" },
    { left: "92%", color: "#ff8a1f", delay: "0.3s"  },
  ];
  return (
    <span aria-hidden className="absolute top-1 left-0 right-0 h-3 pointer-events-none">
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute top-0 w-1.5 h-3 rounded-full animate-hh-streamer"
          style={{
            left: d.left,
            background: d.color,
            animationDelay: d.delay,
            transformOrigin: "top center",
          }}
        />
      ))}
    </span>
  );
}

// Small festive streamer shapes at bottom
function StreamersBottom() {
  const dots = [
    { left: "10%", color: "#4fc3ff", delay: "0.1s"  },
    { left: "26%", color: "#ff4fcf", delay: "0.25s" },
    { left: "42%", color: "#ff8a1f", delay: "0.4s"  },
    { left: "58%", color: "#a78bfa", delay: "0.55s" },
    { left: "74%", color: "#4fc3ff", delay: "0.05s" },
    { left: "88%", color: "#ff4fcf", delay: "0.3s"  },
  ];
  return (
    <span aria-hidden className="absolute bottom-1 left-0 right-0 h-3 pointer-events-none">
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute bottom-0 w-1 h-2.5 rounded-full animate-hh-streamer"
          style={{
            left: d.left,
            background: d.color,
            animationDelay: d.delay,
          }}
        />
      ))}
    </span>
  );
}
